import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  appeler,
  sessionCourante,
  surChangementSession,
  type Session,
} from '../api/client.ts';

/**
 * Session courante et boutique sélectionnée.
 *
 * La boutique est un choix d'INTERFACE, pas une garantie : le serveur vérifie
 * la portée à chaque appel (RG-161). Changer la valeur de ce sélecteur ne
 * donne accès à rien — au mieux on obtient un 403.
 */

interface ValeurContexte {
  session: Session | null;
  /**
   * Boutique selectionnee, ou `null` pour le CONSOLIDE.
   *
   * `null` est la valeur par defaut, pas un etat d'attente : un patron
   * multi-boutique veut d'abord savoir combien l'ensemble a fait.
   */
  boutiqueId: string | null;
  definirBoutique: (id: string | null) => void;
  boutiques: { id: string; code: string; nom: string }[];
  /**
   * État du chargement de la liste — et c'est le point important.
   *
   * La requête ne rendait que `data` : son `error` était jeté. Quand
   * `GET /boutiques` échouait, le sélecteur DISPARAISSAIT purement et
   * simplement, sans un mot. Une panne devenait une absence, et l'utilisateur
   * n'avait aucun moyen de savoir que sa liste n'était pas vide, mais
   * inaccessible.
   *
   * Vu en vrai le 13/09/2026 : la base de développement n'avait pas reçu la
   * migration 019, `GET /boutiques` répondait 500 sur une colonne manquante,
   * et l'écran ne montrait rien d'anormal.
   */
  boutiquesEnCours: boolean;
  erreurBoutiques: unknown;
  rechargerBoutiques: () => void;
  peut: (permission: string) => boolean;
}

const Contexte = createContext<ValeurContexte | null>(null);

export function FournisseurSession({ children }: { children: ReactNode }): ReactNode {
  const [session, setSession] = useState<Session | null>(sessionCourante());
  const [boutiqueId, setBoutiqueId] = useState<string | null>(null);

  useEffect(() => surChangementSession(setSession), []);

  /**
   * Noms des boutiques.
   *
   * La connexion ne rend que des identifiants ; les noms viennent de
   * `GET /boutiques`, filtré par la portée du porteur. Un identifiant seul
   * ne dit rien à un humain.
   */
  const {
    data: boutiques,
    error: erreurBoutiques,
    isPending: boutiquesEnCours,
    refetch: rechargerBoutiques,
  } = useQuery({
    queryKey: ['boutiques', session?.utilisateur.id],
    enabled: session !== null,
    queryFn: () =>
      appeler<{ donnees: { id: string; code: string; nom: string }[] }>('/boutiques'),
    select: (reponse) => reponse.donnees,
  });

  const listeBoutiques = useMemo(() => boutiques ?? [], [boutiques]);

  /*
   * Aucune selection automatique : on RESTE sur le consolide tant que
   * l'utilisateur n'a pas choisi. Retenir la premiere boutique par ordre
   * alphabetique donnerait un chiffre arbitraire, et il faudrait deviner
   * qu'il ne s'agit pas du total.
   */

  // Une session perdue doit effacer la boutique : garder l'ancienne ferait
  // s'ouvrir la session suivante sur une boutique qui n'est peut-être plus
  // dans sa portée.
  useEffect(() => {
    if (session === null) {
      setBoutiqueId(null);
    }
  }, [session]);

  const valeur = useMemo<ValeurContexte>(
    () => ({
      session,
      boutiqueId,
      definirBoutique: setBoutiqueId,
      boutiques: listeBoutiques,
      // `isPending` reste vrai tant que la requête est désactivée : sans la
      // garde sur la session, l'écran de connexion afficherait un chargement.
      boutiquesEnCours: session !== null && boutiquesEnCours,
      erreurBoutiques,
      rechargerBoutiques: () => {
        void rechargerBoutiques();
      },
      peut: (permission) => session?.permissions.includes(permission) ?? false,
    }),
    [
      session,
      boutiqueId,
      listeBoutiques,
      boutiquesEnCours,
      erreurBoutiques,
      rechargerBoutiques,
    ],
  );

  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

export function useSession(): ValeurContexte {
  const valeur = useContext(Contexte);
  if (valeur === null) {
    throw new Error('useSession hors du FournisseurSession.');
  }
  return valeur;
}

/**
 * Boutique sélectionnée, garantie non nulle.
 *
 * Les écrans qui en dépendent ne s'affichent qu'une fois la liste chargée :
 * cela évite une requête initiale sans `boutique_id`, que l'API refuserait.
 */
export function useBoutique(): string | null {
  return useSession().boutiqueId;
}
